import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import { Mark } from "./Mark";

const VIDEOS = [
  "/videos/hero-loop.mp4",
  "/videos/hero-loop-2.mp4",
  "/videos/hero-loop-3.mp4",
];

interface LoaderProps {
  onComplete: () => void;
}

export function Loader({ onComplete }: LoaderProps) {
  const [progress, setProgress] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    let loaded = 0;
    let videosReady = false;
    let minTimePassed = false;
    const startTime = Date.now();

    function tryExit() {
      if (videosReady && minTimePassed) {
        setExiting(true);
        setTimeout(onComplete, 1200);
      }
    }

    function checkDone() {
      loaded++;
      setProgress(Math.round((loaded / VIDEOS.length) * 100));
      if (loaded === VIDEOS.length) {
        videosReady = true;
        tryExit();
      }
    }

    setTimeout(() => {
      minTimePassed = true;
      const elapsed = Date.now() - startTime;
      if (elapsed >= 3000) {
        tryExit();
      } else {
        setTimeout(tryExit, 3000 - elapsed);
      }
    }, 3000);

    VIDEOS.forEach((src) => {
      const video = document.createElement("video");
      video.preload = "auto";
      video.muted = true;
      video.playsInline = true;
      video.addEventListener("canplaythrough", checkDone, { once: true });
      video.src = src;
      video.load();
    });
  }, [onComplete]);

  return (
    <motion.div
      className="fixed inset-0 z-[300] flex flex-col items-center justify-center bg-cream"
      animate={{ opacity: exiting ? 0 : 1 }}
      transition={{ duration: 1.2, ease: "easeInOut" }}
    >
      {/* The mark breathes at the site's shared cadence while the
          footage loads — the first calm moment of the visit. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="breathe mb-8">
          <Mark size={72} />
        </div>
      </motion.div>

      <motion.p
        className="mb-6 font-serif text-lg italic tracking-wide text-warm-brown/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        Unwinding Spirituality
      </motion.p>

      <div className="relative h-px w-48 overflow-hidden bg-warm-brown/10">
        <motion.div
          className="absolute inset-y-0 left-0 bg-gold"
          initial={{ width: "0%" }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      </div>
    </motion.div>
  );
}
